import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { RANK_IMAGES, RANK_ORDER } from '../../utils/rankConstants';
import { getLeaderboardData } from '../../api/userApi';
import { AuthContext } from '../../context/AuthContext';
import '../../Style/Leaderboard.css';
import { getTopUniqueGoals } from '../../utils/goalUtils';
import { CATEGORY_COLORS } from '../../utils/leaderboardConstants';

const GlobalLeaderboardView = () => {
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [globalUsers, setGlobalUsers] = useState([]);

  // Add useMemo to your React imports
  const myData = useMemo(
    () => globalUsers.find((u) => u.id === currentUser?.id),
    [globalUsers, currentUser?.id],
  );

  useEffect(() => {
    const fetchGlobal = async () => {
      try {
        setLoading(true);
        const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const data = await getLeaderboardData('global', currentUser?.id, userTz);

        // Check if the data is wrapped in a Spring Page object
        if (data && Array.isArray(data.content)) {
          // SUCCESS: Grab the array from the 'content' field
          setGlobalUsers(data.content);
        } else if (Array.isArray(data)) {
          // Fallback: if it's already a plain array
          setGlobalUsers(data);
        } else {
          console.error('Data format unknown:', data);
          setGlobalUsers([]);
        }
      } catch (err) {
        console.error('Global LB Error:', err);
        setGlobalUsers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchGlobal();
  }, [currentUser?.id]);

  if (loading) return <div className="lb-empty-state">Loading Global Legends...</div>;

  return (
    <div className="staircase-wrapper">
      <div className="staircase-container">
        {RANK_ORDER.map((rank) => {
          const playersInRank = globalUsers
            .filter((p) => p.rank?.toUpperCase() === rank)
            .sort((a, b) => (b.totalXp || 0) - (a.totalXp || 0));

          return (
            <div key={rank} className={`rank-tier-block tier-${rank.toLowerCase()}`}>
              <div className="tier-header-sticky">
                <img src={RANK_IMAGES[rank]} alt={rank} className="tier-emblem-mini" />
                <span className="tier-name">{rank}</span>
                <span className="tier-count">
                  {playersInRank.length} {playersInRank.length === 1 ? 'User' : 'Users'}
                </span>
              </div>
              <div className="tier-content">
                {playersInRank.length > 0 ? (
                  <div className="player-row-grid">
                    {playersInRank.map((player) => {
                      const isMe = player.id === currentUser?.id;
                      const playerTopGoals = getTopUniqueGoals(player.goals || []);
                      return (
                        <Link
                          to={`/profile/${player.id}`}
                          key={player.id}
                          className={`lb-player-card ${isMe ? 'highlight-me' : ''}`}
                        >
                          <div className="lb-player-avatar">
                            {player.profilePicUrl ? (
                              <img src={player.profilePicUrl} alt="" className="avatar-img" />
                            ) : (
                              player.firstName?.[0] || '?'
                            )}
                          </div>
                          <div
                            className={`lb-player-info ${playerTopGoals.length === 0 ? 'centered-info' : ''}`}
                            style={{ minWidth: 0 }}
                          >
                            <div className="lb-card-top">
                              <span className="lb-player-name">
                                {player.firstName} {player.lastName ? `${player.lastName[0]}.` : ''}
                              </span>
                              {/* If you want XP to stay aligned with the name */}
                              <span className="lb-player-xp">
                                {(player.totalXp || 0).toLocaleString()} <small>XP</small>
                              </span>
                            </div>

                            {playerTopGoals.length > 0 && (
                              <div className="player-badge-row">
                                {playerTopGoals.map((goal, idx) => (
                                  <span
                                    key={idx}
                                    className="skill-tag"
                                    style={{
                                      '--tag-color':
                                        CATEGORY_COLORS[goal.category] || CATEGORY_COLORS.Default,
                                    }}
                                  >
                                    {goal.category}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  /* This renders inside the specific rank block if empty */
                  <div className="lb-empty-state">No players in this tier yet</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {myData && (
        <div className="my-rank-footer">
          <div className="my-rank-content">
            {/* <span className="my-rank-number">#{myGlobalIndex}</span> */}
            <Link to={`/profile/${myData.id}`} className="lb-player-card mini-card">
              <div className="lb-player-avatar">
                {myData.profilePicUrl ? (
                  <img src={myData.profilePicUrl} alt="" className="avatar-img" />
                ) : (
                  myData.firstName?.[0]
                )}
              </div>
              <div className="lb-player-info">
                <div className="lb-card-top">
                  <span className="lb-player-name">
                    {myData.firstName} {myData.lastName ? `${myData.lastName[0]}.` : ''}
                    <span className="my-rank-badges">
                      <small className="you-label">(You)</small>
                      <small className="rank-label">• {myData.rank}</small>
                    </span>
                  </span>
                  <span className="lb-player-xp">{myData.totalXp.toLocaleString()} XP</span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};
export default GlobalLeaderboardView;
